import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';

import { Button, Table } from '../../core-ui';
import {
  HeaderNavigationBar,
  SearchByText,
  SearchHeader,
} from '../../components';
import { colors, spacing } from '../../constants/theme';
import {
  DOWNLOAD_PROGRAM_PARTICIPANT,
  GET_PROGRAM_ADVANCE,
  GET_STORE_PROGRAM_PARTICIPANT,
} from '../../graphql/queries';
import {
  GetStoreProgramParticipants,
  GetStoreProgramParticipantsVariables,
} from '../../generated/GetStoreProgramParticipants';
import {
  DownloadProgramParticipants,
  DownloadProgramParticipantsVariables,
} from '../../generated/DownloadProgramParticipants';
import {
  GetProgramsAdvance,
  GetProgramsAdvanceVariables,
} from '../../generated/GetProgramsAdvance';

export default function StoreAchievementScene() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [storeNameFilter, setStoreNameFilter] = useState<string | null>(null);
  const [managerStoreCodeFilter, setManagerStoreCodeFilter] = useState<
    string | null
  >(null);

  const { data: storeProgram } = useQuery<
    GetProgramsAdvance,
    GetProgramsAdvanceVariables
  >(GET_PROGRAM_ADVANCE, {
    variables: {
      where: {},
    },
  });

  const [
    getProgramParticipants,
    {
      data: dataParticipants,
      loading: participantLoading,
      error: participantError,
    },
  ] = useLazyQuery<
    GetStoreProgramParticipants,
    GetStoreProgramParticipantsVariables
  >(GET_STORE_PROGRAM_PARTICIPANT);

  const [
    downloadProgramParticipants,
    { loading: exportLoading },
  ] = useLazyQuery<
    DownloadProgramParticipants,
    DownloadProgramParticipantsVariables
  >(DOWNLOAD_PROGRAM_PARTICIPANT, {
    onCompleted: ({ downloadProgramParticipants }) => {
      saveAs(
        downloadProgramParticipants.link,
        ' Store Program Participant.csv',
      );
    },
  });

  const currentProgramId = useMemo(
    () => storeProgram?.getProgramsAdvance?.[0]?.id ?? '',
    [storeProgram],
  );

  const buildParticipantVariables = useCallback(
    () => ({
      programId: currentProgramId,
      managerStoreCode: managerStoreCodeFilter || undefined,
      storeName: storeNameFilter || undefined,
    }),
    [currentProgramId, managerStoreCodeFilter, storeNameFilter],
  );

  const buildParticipantVariablesPagination = useCallback(
    () => ({
      ...buildParticipantVariables(),
      pagination: { skip: page * rowsPerPage, first: rowsPerPage },
    }),
    [buildParticipantVariables, page, rowsPerPage],
  );

  useEffect(() => {
    if (currentProgramId) {
      getProgramParticipants({
        variables: buildParticipantVariablesPagination(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProgramId, page, rowsPerPage]);

  const onSearchPress = useCallback(() => {
    if (!currentProgramId) {
      return;
    }
    setPage(0);
    getProgramParticipants({
      variables: buildParticipantVariablesPagination(),
    });
  }, [
    currentProgramId,
    buildParticipantVariablesPagination,
    getProgramParticipants,
  ]);

  const onClearPress = useCallback(() => {
    if (!currentProgramId) {
      return;
    }
    setManagerStoreCodeFilter(null);
    setStoreNameFilter(null);
    setPage(0);
    getProgramParticipants({
      variables: {
        ...buildParticipantVariablesPagination(),
        managerStoreCode: undefined,
        storeName: undefined,
      },
    });
  }, [
    currentProgramId,
    buildParticipantVariablesPagination,
    getProgramParticipants,
  ]);

  const onExportPress = useCallback(() => {
    downloadProgramParticipants({
      variables: buildParticipantVariables(),
    });
  }, [buildParticipantVariables, downloadProgramParticipants]);

  const onRowsChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setRowsPerPage(Number(event.currentTarget.value));
    setPage(0);
  };

  const storeAchievementTableStructure = useMemo(
    () => ({
      managerStoreCode: {
        headerTitle: t(['Kode Toko', 'Store Code']),
        dataPath: 'managerStoreCode',
      },
      storeName: {
        headerTitle: t(['Nama Toko', 'Store Name']),
        dataPath: 'storeName',
      },
    }),
    [],
  );

  const leftSide = () => (
    <>
      <View style={styles.separator}>
        <SearchByText
          label="Cari Kode Toko"
          value={managerStoreCodeFilter || ''}
          setValue={setManagerStoreCodeFilter}
        />
      </View>
      <View style={styles.separator}>
        <SearchByText
          label="Cari Nama Toko"
          value={storeNameFilter || ''}
          setValue={setStoreNameFilter}
        />
      </View>
    </>
  );

  const renderTable = () => (
    <Table
      data={dataParticipants?.getStoreProgramParticipants.row || []}
      dataCount={dataParticipants?.getStoreProgramParticipants.total}
      error={!!participantError}
      structure={storeAchievementTableStructure}
      onChangePage={setPage}
      onChangeRowsPerPage={onRowsChange}
      page={page}
      rowsPerPage={rowsPerPage}
      loading={participantLoading}
    />
  );

  return (
    <View style={styles.root}>
      <HeaderNavigationBar />

      <View style={styles.separator}>
        <SearchHeader
          title="List Peserta"
          onClearPress={onClearPress}
          onSearchPress={onSearchPress}
          containerStyle={styles.searchSection}
          bottomSearchButton
        >
          {leftSide()}
        </SearchHeader>

        <View style={styles.exportButtonContainer}>
          <Button
            data-testid="request-agent-download"
            title={t(['Ekspor Semua Data', 'Export All Data'])}
            onPress={onExportPress}
            style={styles.exportButton}
            isLoading={exportLoading}
          />
        </View>
      </View>
      {renderTable()}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { padding: spacing.xlarge },
  searchSection: {
    paddingBottom: spacing.xsmall,
  },
  separator: {
    paddingBottom: spacing.medium,
    marginBottom: spacing.xxsmall,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.primary,
  },
  exportButtonContainer: {
    justifyContent: 'flex-end',
    flex: 1,
    flexDirection: 'row',
  },
  exportButton: {
    padding: 0,
    minWidth: 200,
  },
});
